import type {
	CreateSensorRequest,
	Sensor,
	SensorLiveStatus,
	SensorSummary,
	UpdateSensorRequest,
} from "@sitescene/types";

import { apiClient, endpoints } from "@/api";

/**
 * Sensors API Service
 *
 * Pure functions for sensor-related API calls.
 * No React dependencies - can be used anywhere.
 *
 * This is the API layer - it only handles HTTP communication.
 * Business logic and state management happen in React Query hooks.
 */

export const sensorsApi = {
	/**
	 * Create a new sensor
	 */
	createSensor: async (sensor: CreateSensorRequest): Promise<Sensor> => {
		const response = await apiClient.post<Sensor>(
			endpoints.sensors.create,
			sensor,
		);
		return response.data;
	},

	/**
	 * Delete a sensor via DELETE /sensors/{sensorId}.
	 *
	 * The backend dry-runs unless `?confirm=true` is passed — without it the
	 * sensor is not removed. Callers that need a real delete must append that
	 * query param (and handle the cleanup preview response).
	 */
	deleteSensor: async (sensorId: string): Promise<void> => {
		await apiClient.delete<void>(endpoints.sensors.delete(sensorId));
	},

	/**
	 * Get status and last input time for every sensor (map pin health)
	 */
	getLiveStatuses: async (): Promise<SensorLiveStatus[]> => {
		const response = await apiClient.get<SensorLiveStatus[]>(
			endpoints.sensors.liveStatus,
		);
		return response.data;
	},

	/**
	 * Get a single sensor by ID
	 */
	getSensor: async (sensorId: string): Promise<Sensor> => {
		const response = await apiClient.get<Sensor>(
			endpoints.sensors.getById(sensorId),
		);
		return response.data;
	},

	/**
	 * Get all sensors
	 */
	getSensors: async (): Promise<Sensor[]> => {
		const response = await apiClient.get<Sensor[]>(endpoints.sensors.get);
		return response.data;
	},

	/**
	 * Get sensors by location ID
	 */
	getSensorsByLocation: async (locationId: string): Promise<Sensor[]> => {
		const response = await apiClient.get<Sensor[]>(
			endpoints.sensors.getByLocation(locationId),
		);
		return response.data;
	},

	/**
	 * Get sensors by multiple location IDs
	 */
	getSensorsByLocations: async (locationIds: string[]): Promise<Sensor[]> => {
		const response = await apiClient.get<Sensor[]>(
			endpoints.sensors.getByLocations(locationIds),
		);
		return response.data;
	},

	/**
	 * Get sensors by zone ID
	 */
	getSensorsByZone: async (zoneId: string): Promise<Sensor[]> => {
		const response = await apiClient.get<Sensor[]>(
			endpoints.sensors.getByZone(zoneId),
		);
		return response.data;
	},

	/**
	 * Get sensor status summary for dashboard
	 * Returns counts of online, offline, warning, and total sensors
	 */
	getSummary: async (): Promise<SensorSummary> => {
		const response = await apiClient.get<SensorSummary>(
			endpoints.sensors.summary,
		);
		return response.data;
	},

	/**
	 * Update an existing sensor
	 */
	updateSensor: async (
		sensorId: string,
		updates: UpdateSensorRequest,
	): Promise<Sensor> => {
		const response = await apiClient.put<Sensor>(
			endpoints.sensors.update(sensorId),
			updates,
		);
		return response.data;
	},
};
